import React, { MouseEvent, useState } from "react";
import {
  Card,
  Group,
  Stack,
  Text,
  Chip,
  Avatar,
  Tooltip,
  Divider,
  ActionIcon,
  CSSProperties,
  Collapse,
  Badge,
  Button,
} from "@mantine/core";
import {
  IconUser,
  IconTruck,
  IconClock,
  IconRoad,
  IconAlertCircle,
  IconCurrencyDollar,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconRoute,
  IconRoute2,
  IconRouteX,
  IconRouteScan,
  IconMapRoute,
} from "@tabler/icons-react";
import { TripResponse, VehicleTripResponse } from "types/trip.types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { ActionFunc } from "./helper";
import { formatMinutes } from "utils/string.helpers";
import { ColorPalette } from "types/ColorSpectrum";
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface VehicleTripCardProps {
  vehicleTrip: VehicleTripResponse;
  current?: boolean;
  timezone?: string;
  onClick?: (tripId: number) => void; // Add optional onClick prop
  onAction?: ActionFunc;
  closeButton?: boolean;
  adminMode?: boolean;
}

export function VehicleTripCard({
  vehicleTrip,
  timezone,
  current,
  onClick,
  onAction,
  closeButton,
  adminMode,
}: VehicleTripCardProps) {
  // const [expanded, setExpanded] = useState(current);

  const onDetailClick = (e: MouseEvent) => {
    e.stopPropagation();
    onAction &&
      onAction("set-tripId-to-display-details", {
        tripId: vehicleTrip.vehicleTripId,
      });
  };

  const startTime = vehicleTrip.startedAt
    ? dayjs(vehicleTrip.startedAt).tz(timezone).format("MMM D, HH:mm")
    : null;
  const duration = vehicleTrip.duration;

  const durationStr = duration !== null ? formatMinutes(duration) : null;
  const actualMiles = vehicleTrip.miles;
  const cardStyle: CSSProperties = { cursor: "pointer", role: "button" };
  if (closeButton) {
    cardStyle.cursor = "default";
  } else if (current) {
    cardStyle.borderColor = "skyblue";
  }

  const colorSpectrum =
    ColorPalette[
      (vehicleTrip.additionalData?.colorIndex ?? 0) % ColorPalette.length
    ];
  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      onClick={() => {
        onClick && onClick(vehicleTrip.vehicleTripId);
      }}
      style={{ ...cardStyle, tabIndex: 0 }}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="start" wrap="nowrap">
          <Stack gap={4}>
            {vehicleTrip.vehicleStops?.length ? (
              <Tooltip label={`#${vehicleTrip.vehicleId}`} withArrow>
                <Text size="xs" c="dimmed">
                  {vehicleTrip.vehicle?.vehicleType}{" "}
                  {vehicleTrip.vehicle?.model}
                </Text>
              </Tooltip>
            ) : (
              <Text size="xs" c="dimmed">
                Unassigned
              </Text>
            )}
            <Group align="center" gap="xs">
              <div
                style={{
                  width: 16,
                  height: 16,
                  border: "1px solid",
                  borderRadius: "50%",
                  borderColor: colorSpectrum.dark40,
                  backgroundColor: colorSpectrum.main,
                }}
              />

              <Tooltip label={`#${vehicleTrip.vehicleTripId}`} withArrow>
                <Text fw={600}>{vehicleTrip.vehicle?.licensePlate}</Text>
              </Tooltip>
            </Group>
            {/* {couriers && (
              <Tooltip label={`#${trip.courier?.courierId}`} withArrow>
                <Group gap={4}>
                  <IconUser size={16} />
                  <Text size="xs">
                    {trip.courier.firstName} {trip.courier.lastName}
                  </Text>
                </Group>
              </Tooltip>
            )} */}
          </Stack>
          <Group gap={4}>
            {closeButton && onAction && (
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction("set-tripId-to-display-details", {
                    tripId: undefined,
                  });
                }}
              >
                <IconX size={18} />
              </ActionIcon>
            )}
          </Group>
        </Group>
        {/* Only show details if expanded */}

        <Collapse in={current ?? false}>
          <Stack gap="xs">
            <Divider />
            {Boolean(vehicleTrip.trips?.length) && (
              <>
                <Group gap="xs">
                  {vehicleTrip.trips?.map((trip, idx) => (
                    <Tooltip label={trip.tripId} withArrow key={idx}>
                      <Text size="xs" m={0} p={0}>
                        {trip.externalId}
                      </Text>
                    </Tooltip>
                  ))}
                </Group>
                <Divider />
              </>
            )}
            <Group gap="md" align="center">
              <Group gap={4}>
                <IconRoad size={16} />
                <Text size="sm">{actualMiles?.toFixed(0)} mi</Text>
              </Group>
              <Group gap={4}>
                <IconClock size={16} />
                <Text size="sm">{durationStr}</Text>
              </Group>
            </Group>

            <Group gap="md" align="center">
              <Group gap={4}>
                <IconCurrencyDollar size={16} />
                <Text size="sm">Total Cost: {/* TODO: fill out */}</Text>
              </Group>
              <Group gap={4}>
                <Text size="sm">Attributed: {/* TODO: fill out */}</Text>
              </Group>
            </Group>
            {!closeButton && (
              <Group gap="md" align="center" justify="end">
                <Button size="xs" variant="transparent" onClick={onDetailClick}>
                  <IconRoute2 size={14} style={{ marginRight: 4 }} />
                  Details
                </Button>
              </Group>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
